import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import Select, { components, ValueType } from 'react-select';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { ErrorInfo, isErrorInfo } from '../shared/interfaces/error-info.interface';
import { getAllAreas } from '../shared/services/data/lookups.data';
import { AreaUiModel } from '../shared/ui-models/area.ui-model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  DailyAvailabilityDayModel,
  DailyAvailabilityQueryModel,
  DailyAvailabilityResponseModel
} from '../shared/models/daily-availability.model';
import { getDailyAvailabilityReport } from './daily-availability.service';

const MODULE_NAME = 'DAILY_AVAILABILITY_MODULE';
const MAX_RANGE_DAYS = 30;

type SelectOption = { value: string; label: string };

const MultiValue = (props: any) => {
  const maxToShow = props.selectProps.maxToShow;
  const length = props.getValue().length;
  const shouldBadgeShow = length > maxToShow;
  const displayLength = length - maxToShow;

  if (props.index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  if (props.index === maxToShow) {
    return shouldBadgeShow ? (
      <div className="text-xs text-neutral-500 px-2 py-1 bg-gray-100 rounded-md">
        + {displayLength} selected
      </div>
    ) : null;
  }

  return null;
};

const normalizeDateTimestamp = (date: Date) => moment(date).startOf('day').valueOf();

const getInclusiveRangeDays = (from: Date, to: Date) =>
  moment(to).startOf('day').diff(moment(from).startOf('day'), 'days') + 1;

export default function DailyAvailabilityContainer() {
  const initialFrom = moment().startOf('day').toDate();
  const initialTo = moment().startOf('day').add(6, 'days').toDate();

  const [loadingData, setLoadingData] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [report, setReport] = useState<DailyAvailabilityResponseModel | undefined>();
  const [areaOptions, setAreaOptions] = useState<SelectOption[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [from, setFrom] = useState<Date>(initialFrom);
  const [to, setTo] = useState<Date>(initialTo);
  const [appliedQuery, setAppliedQuery] = useState<DailyAvailabilityQueryModel | undefined>();
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const selectedAreaOptions = useMemo(
    () => areaOptions.filter((option) => areaIds.includes(option.value)),
    [areaIds, areaOptions]
  );

  const areaNameById = useMemo(
    () =>
      areaOptions.reduce<{ [areaId: string]: string }>((result, area) => {
        result[area.value] = area.label;
        return result;
      }, {}),
    [areaOptions]
  );

  const areaColumns = useMemo(() => {
    const ids: string[] = [];

    areaIds.forEach((areaId) => {
      if (areaId && !ids.includes(areaId)) ids.push(areaId);
    });

    report?.days.forEach((day) => {
      day.areas.forEach((area) => {
        if (area.areaId && !ids.includes(area.areaId)) ids.push(area.areaId);
      });
    });

    return ids.map((areaId) => ({
      areaId,
      label: areaNameById[areaId] || areaId
    }));
  }, [areaIds, areaNameById, report]);

  const filterTags = useMemo(() => {
    const tags: { label: string; value: string }[] = [];

    if (appliedQuery?.areaIds.length) {
      const selectedAreas = appliedQuery.areaIds
        .map((areaId) => areaNameById[areaId] || areaId)
        .join(', ');
      tags.push({ label: 'Areas', value: selectedAreas });
    }

    if (appliedQuery) {
      tags.push({ label: 'From', value: moment(appliedQuery.from).format('MM/DD/YYYY') });
      tags.push({ label: 'To', value: moment(appliedQuery.to).format('MM/DD/YYYY') });
    }

    return tags;
  }, [appliedQuery, areaNameById]);

  const isFilterValid = () => {
    if (!areaIds.length) {
      birdToast.error('Please select at least one area', 'Missing area filter');
      return false;
    }

    if (to.getTime() < from.getTime()) {
      birdToast.error('To date should be after from date', 'Invalid date range');
      return false;
    }

    if (getInclusiveRangeDays(from, to) > MAX_RANGE_DAYS) {
      birdToast.error('Date range cannot exceed 30 days', 'Invalid date range');
      return false;
    }

    return true;
  };

  const load = (query: DailyAvailabilityQueryModel) => {
    setLoadingData(true);
    const promise = getDailyAvailabilityReport(query).then((response) => {
      setLoadingData(false);
      if (isErrorInfo(response)) {
        setReport(undefined);
        return;
      }

      setReport(response);
    });

    birdToast.promise(promise, {
      errorFailedMessage: 'Failed to load daily availability report',
      loadingMessage: 'Loading daily availability report',
      successMessage: 'Daily availability report loaded successfully',
      errorPermissionMessage: 'You do not have permission to view daily availability report'
    });
  };

  const applyFilters = () => {
    if (!isFilterValid()) return;

    const query: DailyAvailabilityQueryModel = {
      from: normalizeDateTimestamp(from),
      to: normalizeDateTimestamp(to),
      areaIds
    };

    setAppliedQuery(query);
    load(query);
  };

  const clearFilters = () => {
    setAreaIds([]);
    setFrom(initialFrom);
    setTo(initialTo);
    setAppliedQuery(undefined);
    setReport(undefined);
  };

  const findAreaBreakdown = (day: DailyAvailabilityDayModel, areaId: string) =>
    day.areas.find((area) => area.areaId === areaId);

  useEffect(() => {
    setLoadingAreas(true);
    getAllAreas(MODULE_NAME).then((response: AreaUiModel[] | ErrorInfo) => {
      setLoadingAreas(false);
      if (!isErrorInfo(response)) {
        setAreaOptions(response.map((area) => ({ value: area.id || '', label: area.name })) || []);
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <div className="flex flex-row items-center justify-between gap-3 mb-4">
        <span className="text-3xl md:text-4xl font-recoleta font-semibold text-neutral-500">
          Daily Availability
        </span>
        <Button
          onClick={() => setFiltersExpanded((prev) => !prev)}
          type={ButtonType.primaryInverted}
          size={ButtonSize.small}
        >
          {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </div>

      <div className="overflow-hidden mb-4">
        <div className="flex flex-col gap-4">
          {filterTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 md:p-4">
              {filterTags.map((tag) => (
                <div
                  key={`${tag.label}-${tag.value}`}
                  className="inline-flex max-w-full items-center gap-1 text-sm md:text-base text-primary-700 font-gilroy-medium bg-white/90 border border-primary-200 px-2 py-0.5 rounded-full"
                >
                  <span className="text-neutral-500">{tag.label}:</span>
                  <span className="truncate max-w-[140px] sm:max-w-[220px]">{tag.value}</span>
                </div>
              ))}
            </div>
          )}

          {filtersExpanded && (
            <div className="p-3 md:p-4 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60 sm:col-span-2 xl:col-span-1">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">Areas *</div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={selectedAreaOptions}
                    options={areaOptions}
                    isLoading={loadingAreas}
                    classNamePrefix="react-select"
                    isMulti
                    maxToShow={2}
                    components={{ MultiValue }}
                    closeMenuOnSelect={false}
                    onChange={(value: ValueType<SelectOption, true>) =>
                      setAreaIds(value?.length ? value.map((option) => option.value) : [])
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">
                    From Date *
                  </div>
                  <ReactDatePicker
                    selected={from}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      const normalizedDate = moment(date).startOf('day').toDate();
                      setFrom(normalizedDate);

                      if (to.getTime() < normalizedDate.getTime()) {
                        setTo(normalizedDate);
                        return;
                      }

                      if (getInclusiveRangeDays(normalizedDate, to) > MAX_RANGE_DAYS) {
                        setTo(
                          moment(normalizedDate)
                            .add(MAX_RANGE_DAYS - 1, 'days')
                            .toDate()
                        );
                      }
                    }}
                    placeholderText="From"
                    className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">To Date *</div>
                  <ReactDatePicker
                    selected={to}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setTo(moment(date).startOf('day').toDate());
                    }}
                    minDate={from}
                    maxDate={moment(from)
                      .add(MAX_RANGE_DAYS - 1, 'days')
                      .toDate()}
                    placeholderText="To"
                    className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4 gap-4">
                <Button
                  onClick={clearFilters}
                  type={ButtonType.primaryInverted}
                  size={ButtonSize.small}
                >
                  Clear
                </Button>
                <Button onClick={applyFilters} size={ButtonSize.small}>
                  Apply Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loadingData ? (
        <LoadingData />
      ) : report?.days.length ? (
        <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden mb-16">
          <div className="w-full overflow-x-auto">
            <table
              className="min-w-[900px] w-full border-separate border-spacing-0"
              aria-label="Daily availability table"
            >
              <thead className="bg-gray-50">
                <tr className="[&>th]:text-xl [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-wider [&>th]:font-gilroy-bold [&>th]:py-3 text-neutral-500 border-b border-gray-200">
                  <th className="px-4 text-left whitespace-nowrap" scope="col">
                    Day
                  </th>
                  <th className="px-4 text-left whitespace-nowrap" scope="col">
                    Available Units
                  </th>
                  <th className="px-4 text-left whitespace-nowrap" scope="col">
                    Total Units
                  </th>
                  {areaColumns.map((area) => (
                    <th className="px-4 text-left whitespace-nowrap" scope="col" key={area.areaId}>
                      <div className="flex flex-col">
                        <span>{area.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.days.map((day, index) => (
                  <tr
                    key={day.dateTimestamp}
                    className={`transition-colors hover:bg-primary-50/40 [&>td]:border-b [&>td]:border-gray-200 [&>td:first-child]:border-l [&>td:last-child]:border-r ${
                      index % 2 ? 'bg-gray-50/40' : 'bg-white'
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap align-middle">
                      <div className="flex flex-col">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {moment(day.date).format('MMM DD, YYYY')}
                        </span>
                        <span className="text-sm text-neutral-400">
                          {moment(day.date).format('dddd')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap align-middle">
                      <span className="font-medium font-gilroy-medium text-neutral-500">
                        {day.availableUnits}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap align-middle">
                      <span className="font-medium font-gilroy-medium text-neutral-500">
                        {day.totalUnits}
                      </span>
                    </td>
                    {areaColumns.map((area) => {
                      const breakdown = findAreaBreakdown(day, area.areaId);
                      return (
                        <td className="px-4 py-3 whitespace-nowrap align-middle" key={area.areaId}>
                          <div className="inline-flex min-w-[118px] overflow-hidden rounded-xl border border-primary-100 bg-white shadow-sm font-gilroy-medium">
                            <div className="flex flex-1 flex-col items-center px-3 py-1.5 bg-primary-50/70">
                              <span className="text-[10px] uppercase tracking-wide text-neutral-400 leading-none">
                                Avail
                              </span>
                              <span className="text-lg leading-5 font-bold text-primary-700">
                                {breakdown?.availableUnits || 0}
                              </span>
                            </div>
                            <div className="w-px bg-primary-100" />
                            <div className="flex flex-1 flex-col items-center px-3 py-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-neutral-400 leading-none">
                                Total
                              </span>
                              <span className="text-lg leading-5 font-bold text-neutral-600">
                                {breakdown?.totalUnits || 0}
                              </span>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <NotFoundData />
      )}
    </div>
  );
}
